/**
 * @fileoverview Liste des paiements backoffice.
 *
 * Table de suivi avec filtres appliqués sur action explicite. Quand une ligne
 * ne possède qu'une seule action principale, elle est directement cliquable.
 * Sélection multiple + rejeu d'enregistrement Quipux en masse (observation
 * Quipux C-12) ; recherche et colonnes enrichies (C-09, C-11).
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Amount,
  Button,
  Checkbox,
  DataCard,
  DataTable,
  EmptyState,
  FormField,
  Input,
  OPERATOR_LABELS,
  OperatorLogo,
  PageHeader,
  SearchInput,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  StatusBadge,
} from '@gnpc/ui';
import { CircleHelp } from 'lucide-react';
import {
  ADMIN_PAYMENTS,
  formatDateTime,
  maskPhone,
  type AdminPayment,
} from '../data/backoffice.ts';

/** Taille de page côté mock — volontairement petite pour exercer la pagination. */
const PAGE_SIZE = 4;

/**
 * Instant de référence (mock) pour calculer l'âge des anomalies — aligné sur
 * l'horizon des données de démonstration. Au branchement API, `Date.now()`.
 */
const REFERENCE_NOW = new Date('2026-07-14T09:00:00Z').getTime();

interface PaymentFilters {
  query: string;
  sgiFilter: string;
  paymentFilter: string;
  operatorFilter: string;
  dateFrom: string;
  dateTo: string;
  /** Filtre rapide : ne montrer que les anomalies d'enregistrement (C-10). */
  anomaliesOnly: boolean;
  amountMin: string;
  amountMax: string;
}

const EMPTY_FILTERS: PaymentFilters = {
  query: '',
  sgiFilter: 'ALL',
  paymentFilter: 'ALL',
  operatorFilter: 'ALL',
  dateFrom: '',
  dateTo: '',
  anomaliesOnly: false,
  amountMin: '',
  amountMax: '',
};

/**
 * Anomalie d'enregistrement = paiement encaissé (PAID) dont l'enregistrement
 * chez Quipux n'est pas complet (échec, partiel ou en attente). Alimente le
 * filtre rapide « anomalies uniquement » et la colonne « âge de l'anomalie »
 * (observations Quipux C-10, C-11).
 */
function isRegistrationAnomaly(payment: AdminPayment): boolean {
  return (
    payment.paymentStatus === 'PAID' &&
    (payment.sgiStatus === 'FAILED' ||
      payment.sgiStatus === 'PARTIAL' ||
      payment.sgiStatus === 'PENDING')
  );
}

/** Âge écoulé depuis la création d'une anomalie, formaté « X h » ou « X j ». */
function formatAnomalyAge(payment: AdminPayment): string {
  if (!isRegistrationAnomaly(payment)) return '—';
  const elapsedMs = REFERENCE_NOW - new Date(payment.createdAt).getTime();
  if (elapsedMs <= 0) return '—';
  const hours = Math.floor(elapsedMs / 3_600_000);
  return hours < 24 ? `${Math.max(hours, 1)} h` : `${Math.floor(hours / 24)} j`;
}

export function PaymentsPage(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialFilters: PaymentFilters = {
    ...EMPTY_FILTERS,
    sgiFilter: params.get('sgi_status') ?? 'ALL',
  };
  const [draftFilters, setDraftFilters] = useState<PaymentFilters>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<PaymentFilters>(initialFilters);
  /** Index de page (base 0), remis à zéro à chaque changement de filtres. */
  const [pageIndex, setPageIndex] = useState(0);
  /** Sélection multiple (par id) pour le rejeu d'enregistrement en masse. */
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  /** Message de confirmation de la dernière action en masse. */
  const [bulkNotice, setBulkNotice] = useState<string | null>(null);

  const updateDraftFilter = <K extends keyof PaymentFilters>(
    key: K,
    value: PaymentFilters[K],
  ) => {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  };

  const filteredPayments = useMemo(() => {
    return ADMIN_PAYMENTS.filter((payment) => {
      const normalizedQuery = appliedFilters.query.toLowerCase();
      // Recherche élargie (C-09) : réf. marchand, code de paiement, réf. de
      // transaction opérateur, n° de matricule, plaque et téléphone.
      const matchesQuery =
        normalizedQuery.length === 0 ||
        payment.merchantReference.toLowerCase().includes(normalizedQuery) ||
        payment.lineItems.some((line) =>
          line.liquidationNumber.toLowerCase().includes(normalizedQuery),
        ) ||
        payment.matricule.toLowerCase().includes(normalizedQuery) ||
        payment.plate.toLowerCase().includes(normalizedQuery) ||
        (payment.operatorReference?.toLowerCase().includes(normalizedQuery) ?? false) ||
        payment.userPhone.includes(appliedFilters.query);
      const matchesSgi =
        appliedFilters.sgiFilter === 'ALL' || payment.sgiStatus === appliedFilters.sgiFilter;
      const matchesPayment =
        appliedFilters.paymentFilter === 'ALL' ||
        payment.paymentStatus === appliedFilters.paymentFilter;
      // `NONE` cible les transactions sans opérateur (usager jamais passé
      // au guichet) — un état légitime du contrat, pas une donnée manquante.
      const matchesOperator =
        appliedFilters.operatorFilter === 'ALL' ||
        (appliedFilters.operatorFilter === 'NONE'
          ? payment.operatorCode === null
          : payment.operatorCode === appliedFilters.operatorFilter);
      const paymentDate = payment.createdAt.slice(0, 10);
      const matchesDate =
        (!appliedFilters.dateFrom || paymentDate >= appliedFilters.dateFrom) &&
        (!appliedFilters.dateTo || paymentDate <= appliedFilters.dateTo);
      const matchesAnomalies = !appliedFilters.anomaliesOnly || isRegistrationAnomaly(payment);
      const min = appliedFilters.amountMin ? Number(appliedFilters.amountMin) : null;
      const max = appliedFilters.amountMax ? Number(appliedFilters.amountMax) : null;
      const matchesAmount =
        (min === null || payment.totalAmount >= min) &&
        (max === null || payment.totalAmount <= max);
      return (
        matchesQuery &&
        matchesSgi &&
        matchesPayment &&
        matchesOperator &&
        matchesDate &&
        matchesAnomalies &&
        matchesAmount
      );
    });
  }, [appliedFilters]);

  /**
   * Découpage local du mock : la vraie API utilisera un curseur opaque, mais
   * la surface UI (`hasNextPage`/`hasPreviousPage`, libellé « X–Y sur Z »)
   * reste identique au branchement backend.
   */
  const pagePayments = useMemo(
    () => filteredPayments.slice(pageIndex * PAGE_SIZE, (pageIndex + 1) * PAGE_SIZE),
    [filteredPayments, pageIndex],
  );
  const hasPreviousPage = pageIndex > 0;
  const hasNextPage = (pageIndex + 1) * PAGE_SIZE < filteredPayments.length;
  const rangeStart = filteredPayments.length === 0 ? 0 : pageIndex * PAGE_SIZE + 1;
  const rangeEnd = Math.min(filteredPayments.length, (pageIndex + 1) * PAGE_SIZE);

  const pageIds = pagePayments.map((payment) => payment.id);
  const pageAllSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const pageSomeSelected = !pageAllSelected && pageIds.some((id) => selectedIds.has(id));

  function toggleOne(id: string, checked: boolean): void {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function togglePage(checked: boolean): void {
    setSelectedIds((current) => {
      const next = new Set(current);
      for (const id of pageIds) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }

  function replaySelection(): void {
    setBulkNotice(
      `Rejeu d'enregistrement Quipux demandé pour ${selectedIds.size} paiement(s).`,
    );
    setSelectedIds(new Set());
  }

  // Colonnes construites dans le composant : la colonne de sélection doit
  // accéder à l'état de sélection et à ses handlers.
  const columns = useMemo<ColumnDef<AdminPayment>[]>(
    () => [
      {
        id: 'select',
        header: () => (
          <Checkbox
            aria-label="Tout sélectionner sur cette page"
            checked={pageAllSelected ? true : pageSomeSelected ? 'indeterminate' : false}
            onCheckedChange={(value) => togglePage(value === true)}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            aria-label={`Sélectionner le paiement ${row.original.merchantReference}`}
            checked={selectedIds.has(row.original.id)}
            onCheckedChange={(value) => toggleOne(row.original.id, value === true)}
          />
        ),
      },
      {
        accessorKey: 'merchantReference',
        header: 'Référence',
        cell: ({ row }) => (
          <div className="grid gap-1">
            <code className="font-mono text-xs">{row.original.merchantReference}</code>
            <span className="text-muted-foreground text-xs">{row.original.plate}</span>
          </div>
        ),
      },
      {
        accessorKey: 'userPhone',
        header: 'Téléphone',
        cell: ({ row }) => (
          <span className="font-mono text-xs">{maskPhone(row.original.userPhone)}</span>
        ),
      },
      {
        accessorKey: 'operatorCode',
        header: 'Opérateur',
        cell: ({ row }) => (
          <div className="grid gap-1">
            <OperatorLogo code={row.original.operatorCode} size="sm" withLabel />
            {row.original.operatorReference ? (
              <span className="text-muted-foreground font-mono text-[11px]">
                {row.original.operatorReference}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        accessorKey: 'paymentStatus',
        header: 'Statut du paiement',
        cell: ({ row }) => (
          <StatusBadge kind="payment" status={row.original.paymentStatus} compact />
        ),
      },
      {
        accessorKey: 'sgiStatus',
        header: 'Enregistrement Quipux',
        cell: ({ row }) => <StatusBadge kind="sgi" status={row.original.sgiStatus} compact />,
      },
      {
        accessorKey: 'recordedAt',
        header: 'Enregistré Quipux',
        cell: ({ row }) => (
          <span className="text-sm">
            {row.original.recordedAt ? formatDateTime(row.original.recordedAt) : '—'}
          </span>
        ),
      },
      {
        id: 'anomalyAge',
        header: 'Âge anomalie',
        cell: ({ row }) => {
          const age = formatAnomalyAge(row.original);
          return (
            <span
              className={
                age === '—'
                  ? 'text-muted-foreground text-sm'
                  : 'text-status-failed-800 text-sm font-medium tabular-nums'
              }
            >
              {age}
            </span>
          );
        },
      },
      {
        accessorKey: 'totalAmount',
        header: 'Montant',
        cell: ({ row }) => <Amount value={row.original.totalAmount} />,
      },
      {
        accessorKey: 'createdAt',
        header: 'Créé le',
        cell: ({ row }) => (
          <span className="text-sm">{formatDateTime(row.original.createdAt)}</span>
        ),
      },
    ],
    [pageAllSelected, pageSomeSelected, selectedIds],
  );

  const applyFilters = () => {
    setAppliedFilters({ ...draftFilters, query: draftFilters.query.trim() });
    setPageIndex(0);
  };

  const resetFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPageIndex(0);
  };

  return (
    <Sheet>
      <div className="grid gap-6">
        <PageHeader
          eyebrow="Paiements"
          title="Suivre les paiements"
          description="Consulter les transactions, isoler les anomalies d'enregistrement Quipux et ouvrir les détails nécessaires au rejeu."
          actions={
            <SheetTrigger asChild>
              <Button variant="outline">
                <CircleHelp className="size-4" />
                Comprendre les statuts
              </Button>
            </SheetTrigger>
          }
        />

      <DataTable
        columns={columns}
        data={pagePayments}
        rowAction={{
          label: (payment) => `Ouvrir le détail du paiement ${payment.merchantReference}`,
          onSelect: (payment) => navigate(`/payments/${payment.id}`),
        }}
        toolbar={
          <div className="grid w-full gap-3">
            <form
              className="grid w-full items-end gap-3 sm:grid-cols-2 lg:grid-cols-4"
              onSubmit={(event) => {
                event.preventDefault();
                applyFilters();
              }}
            >
              <FormField label="Recherche" className="lg:col-span-2">
                <SearchInput
                  size="sm"
                  value={draftFilters.query}
                  onChange={(event) => updateDraftFilter('query', event.target.value)}
                  onClear={() => updateDraftFilter('query', '')}
                  placeholder="Réf., code de paiement, réf. opérateur, matricule, plaque, téléphone…"
                />
              </FormField>
              <FormField label="Enregistrement Quipux">
                <select
                  value={draftFilters.sgiFilter}
                  onChange={(event) => updateDraftFilter('sgiFilter', event.target.value)}
                  className="border-input bg-background h-9 min-w-0 rounded-md border px-3 text-sm"
                >
                  <option value="ALL">Tous (enregistrement)</option>
                  <option value="FAILED">Échec d'enregistrement</option>
                  <option value="PENDING">En attente d'enregistrement</option>
                  <option value="PARTIAL">Partiel</option>
                  <option value="RECORDED">Enregistré</option>
                  <option value="NOT_APPLICABLE">Non applicable</option>
                </select>
              </FormField>
              <FormField label="Statut du paiement">
                <select
                  value={draftFilters.paymentFilter}
                  onChange={(event) => updateDraftFilter('paymentFilter', event.target.value)}
                  className="border-input bg-background h-9 min-w-0 rounded-md border px-3 text-sm"
                >
                  <option value="ALL">Tous paiements</option>
                  <option value="PAID">Payé</option>
                  <option value="PENDING">En attente</option>
                  <option value="FAILED">Échoué</option>
                  <option value="CANCELLED">Annulé</option>
                  <option value="EXPIRED">Expiré</option>
                </select>
              </FormField>
              <FormField label="Opérateur">
                <select
                  value={draftFilters.operatorFilter}
                  onChange={(event) => updateDraftFilter('operatorFilter', event.target.value)}
                  className="border-input bg-background h-9 min-w-0 rounded-md border px-3 text-sm"
                >
                  <option value="ALL">Tous opérateurs</option>
                  {Object.entries(OPERATOR_LABELS).map(([code, label]) => (
                    <option key={code} value={code}>
                      {label}
                    </option>
                  ))}
                  <option value="NONE">Non renseigné</option>
                </select>
              </FormField>
              <FormField label="Montant min (F CFA)">
                <Input
                  size="sm"
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={draftFilters.amountMin}
                  onChange={(event) => updateDraftFilter('amountMin', event.target.value)}
                  placeholder="0"
                />
              </FormField>
              <FormField label="Montant max (F CFA)">
                <Input
                  size="sm"
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={draftFilters.amountMax}
                  onChange={(event) => updateDraftFilter('amountMax', event.target.value)}
                  placeholder="Illimité"
                />
              </FormField>
              <FormField label="Date de début">
                <Input
                  size="sm"
                  type="date"
                  value={draftFilters.dateFrom}
                  onChange={(event) => updateDraftFilter('dateFrom', event.target.value)}
                />
              </FormField>
              <FormField label="Date de fin">
                <Input
                  size="sm"
                  type="date"
                  value={draftFilters.dateTo}
                  onChange={(event) => updateDraftFilter('dateTo', event.target.value)}
                />
              </FormField>
              <div className="flex flex-wrap items-center justify-between gap-3 sm:col-span-2 lg:col-span-4">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-quipux-navy-950">
                  <Checkbox
                    checked={draftFilters.anomaliesOnly}
                    onCheckedChange={(value) =>
                      updateDraftFilter('anomaliesOnly', value === true)
                    }
                  />
                  Anomalies uniquement
                </label>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Button type="submit" size="sm">
                    Rechercher
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={resetFilters}>
                    Réinitialiser
                  </Button>
                  <Button asChild size="sm" variant="outline">
                    <Link to="/exports">Exporter</Link>
                  </Button>
                </div>
              </div>
            </form>

            {/* Barre d'action de masse (C-12) — visible dès qu'au moins un
              * paiement est sélectionné. Le rejeu réel est côté backend ; ici
              * la sélection + la demande de rejeu groupé sont matérialisées. */}
            {selectedIds.size > 0 ? (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-muted px-4 py-2.5">
                <span className="text-sm font-medium text-quipux-navy-950">
                  {selectedIds.size} paiement(s) sélectionné(s)
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" onClick={replaySelection}>
                    Rejouer l'enregistrement Quipux
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setSelectedIds(new Set())}>
                    Désélectionner
                  </Button>
                </div>
              </div>
            ) : null}

            {bulkNotice ? (
              <p
                role="status"
                className="border-status-paid/30 bg-status-paid/10 text-status-paid-800 rounded-md border px-4 py-2 text-sm"
              >
                {bulkNotice}
              </p>
            ) : null}
          </div>
        }
        emptyState={
          <EmptyState
            title="Aucun paiement dans cette vue"
            description="Modifiez les filtres ou la recherche pour élargir le périmètre."
            action={
              <Button variant="outline" size="sm" onClick={resetFilters}>
                Réinitialiser les filtres
              </Button>
            }
          />
        }
        mobileCardLabel="Paiements"
        mobileCard={(payment) => (
          <DataCard
            eyebrow={payment.merchantReference}
            title={payment.plate}
            description={`${maskPhone(payment.userPhone)} · ${
              payment.operatorCode ? OPERATOR_LABELS[payment.operatorCode] : 'Opérateur non renseigné'
            }`}
            aside={<Amount value={payment.totalAmount} />}
            meta={
              <>
                <StatusBadge kind="payment" status={payment.paymentStatus} compact />
                <StatusBadge kind="sgi" status={payment.sgiStatus} compact />
                <span className="text-muted-foreground">{formatDateTime(payment.createdAt)}</span>
              </>
            }
          />
        )}
        pagination={{
          label: `${rangeStart}–${rangeEnd} sur ${filteredPayments.length}`,
          hasPreviousPage,
          hasNextPage,
          onPreviousPage: () => setPageIndex((current) => Math.max(0, current - 1)),
          onNextPage: () => setPageIndex((current) => current + 1),
        }}
      />
      </div>

      {/* Sheet d'aide : explique chaque statut de paiement et d'enregistrement
        * Quipux (inclut la définition du « Partiel » demandée en C-13). */}
      <SheetContent className="w-[calc(100vw-2rem)] overflow-y-auto p-0 sm:max-w-md">
        <SheetHeader className="border-border border-b p-5 pr-12">
          <SheetTitle>Comprendre les statuts</SheetTitle>
          <SheetDescription>
            Signification des statuts de paiement et d'enregistrement Quipux.
          </SheetDescription>
        </SheetHeader>

        <div className="grid gap-6 p-5">
          <section className="grid gap-3">
            <h3 className="text-quipux-navy-950 text-sm font-semibold">Statut du paiement</h3>
            <dl className="grid gap-3 text-sm">
              <div className="grid grid-cols-[9rem_minmax(0,1fr)] items-start gap-3">
                <StatusBadge kind="payment" status="PAID" />
                <p className="text-muted-foreground">
                  Confirmé par l'opérateur — l'argent est encaissé.
                </p>
              </div>
              <div className="grid grid-cols-[9rem_minmax(0,1fr)] items-start gap-3">
                <StatusBadge kind="payment" status="PENDING" />
                <p className="text-muted-foreground">
                  Initié, en attente de confirmation de l'opérateur.
                </p>
              </div>
              <div className="grid grid-cols-[9rem_minmax(0,1fr)] items-start gap-3">
                <StatusBadge kind="payment" status="FAILED" />
                <p className="text-muted-foreground">N'a pas abouti — aucun montant débité.</p>
              </div>
              <div className="grid grid-cols-[9rem_minmax(0,1fr)] items-start gap-3">
                <StatusBadge kind="payment" status="EXPIRED" />
                <p className="text-muted-foreground">Session de paiement expirée sans débit.</p>
              </div>
            </dl>
          </section>

          <section className="grid gap-3">
            <h3 className="text-quipux-navy-950 text-sm font-semibold">Enregistrement Quipux</h3>
            <dl className="grid gap-3 text-sm">
              <div className="grid grid-cols-[9rem_minmax(0,1fr)] items-start gap-3">
                <StatusBadge kind="sgi" status="RECORDED" />
                <p className="text-muted-foreground">
                  Toutes les lignes du paiement sont enregistrées chez Quipux.
                </p>
              </div>
              <div className="grid grid-cols-[9rem_minmax(0,1fr)] items-start gap-3">
                <StatusBadge kind="sgi" status="PARTIAL" />
                <p className="text-muted-foreground">
                  Une partie des lignes est enregistrée ; le reste est en échec ou en attente.
                </p>
              </div>
              <div className="grid grid-cols-[9rem_minmax(0,1fr)] items-start gap-3">
                <StatusBadge kind="sgi" status="PENDING" />
                <p className="text-muted-foreground">Enregistrement en cours ou en file de rejeu.</p>
              </div>
              <div className="grid grid-cols-[9rem_minmax(0,1fr)] items-start gap-3">
                <StatusBadge kind="sgi" status="FAILED" />
                <p className="text-muted-foreground">
                  Enregistrement en échec — un rejeu est nécessaire.
                </p>
              </div>
              <div className="grid grid-cols-[9rem_minmax(0,1fr)] items-start gap-3">
                <StatusBadge kind="sgi" status="NOT_APPLICABLE" />
                <p className="text-muted-foreground">
                  Aucun enregistrement attendu (ex. paiement non confirmé).
                </p>
              </div>
            </dl>
          </section>
        </div>

        <SheetFooter className="border-border border-t p-5">
          <SheetClose asChild>
            <Button variant="outline">Fermer</Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
